import React, { useLayoutEffect } from 'react';

import { Box, Button, Center } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import type {
	MRT_ColumnDef,
	MRT_ColumnFiltersState,
	MRT_ColumnOrderState,
	MRT_ColumnSizingState,
	MRT_PaginationState,
	MRT_SortingState,
	MRT_TableOptions,
	MRT_Updater,
	MRT_VisibilityState,
} from 'mantine-react-table';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import type { TFEmptyStateMessageProps } from '../../../main';
import { Icon, TFCard, TFEmptyStateMessage } from '../../../main';
import type { FilterItem } from '../TFDataFilters';
import { ActiveFilters, FilterButton, SearchInput } from '../TFDataFilters';

import { VisibilityButton } from './VisibilityButton';

const useStyles = createStyles(() => ({
	sorter: {
		display: 'flex',
		alignItems: 'center',
		padding: '0.45rem 0.7rem',
		userSelect: 'none',
		transition: '.05s ease-in-out',
	},
	searchBar: {
		padding: '.75rem',
		display: 'flex',
	},
}));

interface Props<TData extends Record<string, any>> {
	name: string;
	data: TData[];
	defs: MRT_ColumnDef<TData>[];
	hideSearchbar?: boolean;
	searchPlaceholder?: string;
	availableFilters?: FilterItem[];
	tableProps?: MRT_TableOptions<TData>['mantineTableProps'];
	paperProps?: MRT_TableOptions<TData>['mantinePaperProps'];
	isLoading?: boolean;
	serverPagination?: {
		pageSize: number;
		pageIndex: number;
		rowCount: number;
		onPaginationChange: (state: MRT_Updater<MRT_PaginationState>) => void;
	};
	serverSorting?: {
		state: MRT_SortingState;
		onSortingChange: (state: MRT_Updater<MRT_SortingState>) => void;
	};
	serverFiltering?: {
		state: MRT_ColumnFiltersState;
		onFiltersChange: (state: MRT_Updater<MRT_ColumnFiltersState>) => void;
	};
	serverSearch?: {
		state: string;
		onSearchChange: (state: string) => void;
	};
	withClientPagination?: boolean;
	noFilteredDataMessage?: TFEmptyStateMessageProps;
	noDataMessage?: TFEmptyStateMessageProps;
}

export function TFDataTable<T extends Record<string, any>>({
	name,
	data,
	defs,
	hideSearchbar,
	availableFilters = [],
	tableProps,
	paperProps,
	isLoading,
	serverPagination,
	serverSorting,
	serverFiltering,
	serverSearch,
	withClientPagination,
	noFilteredDataMessage,
	noDataMessage,
	searchPlaceholder,
}: Props<T>) {
	const { classes } = useStyles();

	const nameToKeyMapping: Record<any, string> = {};
	const initialVisibilityState: Record<any, boolean> = {};
	const initialOrderState: string[] = [];

	defs.forEach((column) => {
		const key = column.id ?? column.accessorKey;
		if (key === undefined) {
			throw new Error('no column id or accessorKey');
		}

		nameToKeyMapping[key] = column.header;
		initialVisibilityState[key] = true;
		initialOrderState.push(key as string);
	});

	const [searchValue, setSearchValue] = useLocalStorage<string>({
		defaultValue: serverSearch ? undefined : '',
		key: `${name}-searchValue`,
	});

	const searchHandler = serverSearch ? serverSearch.onSearchChange : setSearchValue;

	const defaultNoDataMessage: TFEmptyStateMessageProps = {
		icon: <Icon.IconSearchOff />,
		title: 'Nothing found',
	};

	const [debouncedValue] = useDebouncedValue(searchValue, 200);

	const [filters, setFilters] = useLocalStorage<MRT_ColumnFiltersState>({
		defaultValue: [],
		key: `${name}-filters`,
	});

	const filterHandler = serverFiltering?.onFiltersChange || setFilters;

	const clearFiltersButton = (
		<Button
			variant="light"
			size="md"
			leftSection={<Icon.IconFilterOff size={18} />}
			onClick={() => {
				searchHandler('');
				filterHandler([]);
			}}
		>
			Clear filters
		</Button>
	);

	const defaultNoFilteredDataMessage: TFEmptyStateMessageProps = {
		icon: <Icon.IconSearchOff />,
		title: 'Nothing found',
		description: 'Please check spelling or reset filters',
		primaryAction: clearFiltersButton,
	};

	const noFilteredData = noFilteredDataMessage
		? { ...defaultNoFilteredDataMessage, ...noFilteredDataMessage }
		: defaultNoFilteredDataMessage;

	const noData = noDataMessage ? { ...defaultNoDataMessage, ...noDataMessage } : defaultNoDataMessage;

	const [sorting, setSorting] = useLocalStorage<MRT_SortingState>({
		defaultValue: [],
		key: `${name}-sorting`,
	});

	// For some reason, the sorting state resets to default on every initial table render. This trick helps to maintain persistent sorting
	const sortState = JSON.parse(localStorage.getItem(`${name}-sorting`) || '[]');
	useLayoutEffect(() => {
		if (sortState.length > 0) {
			setSorting(sortState);
		}
	}, []);

	const [order, setOrder] = useLocalStorage<MRT_ColumnOrderState>({
		defaultValue: initialOrderState,
		key: `${name}-order`,
	});

	const [sizing, setSizing] = useLocalStorage<MRT_ColumnSizingState>({
		defaultValue: {},
		key: `${name}-sizing`,
	});

	const [clientPaginationState, setClientPaginationState] = useLocalStorage<MRT_PaginationState>({
		defaultValue: { pageIndex: 0, pageSize: 10 },
		key: `${name}-client-pagination`,
		getInitialValueInEffect: false,
	});

	const [storedVisibility, setVisibility] = useLocalStorage<MRT_VisibilityState>({
		defaultValue: initialVisibilityState,
		key: `${name}-visibility`,
	});
	// We can add or remove table columns, so old settings may not work
	// Merging stored settings and actual defaults here
	const visibility: MRT_VisibilityState = Object.fromEntries(
		Object.entries(initialVisibilityState).map(([column, state]) => [
			column,
			storedVisibility[column] ?? state,
		])
	);

	const table = useMantineReactTable({
		columns: defs,
		data,
		onColumnOrderChange: setOrder,
		onColumnVisibilityChange: setVisibility,
		onColumnSizingChange: setSizing,
		onSortingChange: serverSorting?.onSortingChange || setSorting,
		onPaginationChange:
			serverPagination?.onPaginationChange ||
			(withClientPagination ? setClientPaginationState : undefined),

		state: {
			sorting: serverSorting?.state?.length
				? [{ id: serverSorting.state[0].id, desc: serverSorting.state[0].desc }]
				: sorting,
			globalFilter: serverSearch ? undefined : debouncedValue,
			columnFilters: filters,
			columnOrder: order,
			columnVisibility: visibility,
			columnSizing: sizing,
			isLoading: isLoading,
			pagination: serverPagination
				? { pageSize: serverPagination.pageSize, pageIndex: serverPagination.pageIndex }
				: withClientPagination
				? clientPaginationState
				: { pageSize: Math.max(data.length, 1), pageIndex: 0 },
		},
		layoutMode: 'grid',
		enableColumnOrdering: true,
		enableColumnResizing: true,
		enableSorting: true,
		columnFilterDisplayMode: 'custom',
		enableBottomToolbar: Boolean(serverPagination) || Boolean(withClientPagination),
		manualPagination: Boolean(serverPagination),
		manualSorting: Boolean(serverSorting),
		paginationDisplayMode: 'pages',
		enableTopToolbar: false,
		enableColumnActions: false,
		rowCount: serverPagination?.rowCount,

		mantineTableProps: {
			verticalSpacing: 5,
			striped: 'odd',
			fz: '13px',

			...tableProps,
		},
		mantineLoadingOverlayProps: {
			zIndex: 300,
		},
		mantineTableBodyRowProps: {
			sx: {
				'--mrt-row-hover-background-color': '#f2f4f7',
				'--mrt-striped-row-hover-background-color': '#f2f4f7',
				'--mrt-striped-row-background-color': '#f9fafb',
			},
		},
		mantinePaperProps: {
			withBorder: false,
			sx: { borderRadius: '8px', boxShadow: 'none' },
			...paperProps,
		},
		mantineTableHeadCellProps: {
			fw: 600,
			color: '#344054',
			sx: {
				'[class*="sort-button"]': {
					marginLeft: '4px',
					opacity: '0.4',
				},
				'[data-sorted="asc"], [data-sorted="desc"]': {
					color: '#155eef',
					opacity: '1',
				},
				'[class*="resize-handle"]': {
					width: '1px',
					opacity: '0.4',
					padding: '0 2px',
					borderRight: '1px solid black',
					borderRadius: 0,
					backgroundColor: 'transparent',
				},
				'[class*="grab-handle"]': {
					opacity: '0.4',
				},
			},
		},
		mantineHighlightProps: {
			sx: {
				fontSize: 'inherit',
			},
		},
		mantineBottomToolbarProps: {
			sx: {
				'[class*="Select-wrapper"]': {
					width: '80px',
				},
				'[class*="Pagination-module_root"]': {
					padding: '10px 12px',
				},
			},
		},
	});

	const { rows } = table.getRowModel();

	const hasActiveFilters = (serverFiltering?.state || filters).length > 0;
	const hasVisibleColumns = Object.values(visibility).some(Boolean);
	const currentSearchValue = serverSearch ? serverSearch.state : searchValue;
	const hasSearchValue = Boolean(currentSearchValue);
	const hasAnyFiltering = hasActiveFilters || hasSearchValue;
	const isNoData = data.length === 0 && !hasAnyFiltering;

	return (
		<>
			{!isNoData && !hideSearchbar && (
				<Box mb=".75rem">
					<TFCard>
						<Box className={classes.searchBar}>
							<SearchInput
								placeholder={searchPlaceholder}
								autoFocus
								searchValue={currentSearchValue}
								onSearchChange={searchHandler}
							/>
							<VisibilityButton
								visibilityOptions={visibility}
								onVisibilityChange={setVisibility}
								nameToKeyMapping={nameToKeyMapping}
							/>
							<FilterButton
								availableFilters={availableFilters}
								activeFilters={serverFiltering?.state || filters}
								setFilters={filterHandler}
							/>
						</Box>
						<ActiveFilters
							availableFilters={availableFilters}
							activeFilters={serverFiltering?.state || filters}
							setFilters={filterHandler}
						/>
					</TFCard>
				</Box>
			)}

			<TFCard>
				{isLoading || (rows.length > 0 && hasVisibleColumns) ? (
					<MantineReactTable table={table} />
				) : (
					<Center h={300}>
						{hasAnyFiltering ? (
							<TFEmptyStateMessage {...noFilteredData} />
						) : (
							<TFEmptyStateMessage {...noData} />
						)}
					</Center>
				)}
			</TFCard>
		</>
	);
}
